import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { filter, fromEvent, of, switchMap, tap } from 'rxjs';

import { useSubscriptions } from '@/hook';

import { POPOVER_PLACEMENT, POPOVER_TRIGGER } from './constant';
import VBasePopoverContext from './context';
import type { VBasePopoverContextValue, VBasePopoverProps } from './type';

export const VBasePopoverContextProvider = ({
  children,
  disabled,
  placement: _placement,
  trigger: _trigger,
  visible: _visible = false,
  onUpdateVisible,
  ...rest
}: PropsWithChildren<VBasePopoverProps>) => {
  const placement = useMemo(
    () => _placement ?? POPOVER_PLACEMENT.TOP,
    [_placement],
  );
  const trigger = useMemo(() => _trigger ?? POPOVER_TRIGGER.HOVER, [_trigger]);
  const [visible, setVisible] = useState(_visible);

  const hide = () => setVisible(false);
  const show = () => setVisible(true);
  const toggle = () => setVisible(v => !v);

  const $popover = useRef<HTMLDivElement>(null);
  const $target = useRef<HTMLElement>(null);

  const setRef = <
    N extends keyof Pick<VBasePopoverContextValue, '$popover' | '$target'>,
  >(
    refName: N,
    value: VBasePopoverContextValue[N]['current'],
  ) => {
    switch (refName) {
      case '$popover':
        $popover.current = value as HTMLDivElement;
        break;
      case '$target':
        $target.current = value as HTMLElement;
        break;
    }
  };

  useEffect(() => {
    if (visible === _visible) return;

    onUpdateVisible?.(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible === _visible) return;

    setVisible(_visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_visible]);

  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'click')
        .pipe(
          filter(() => !disabled),
          filter(() => trigger === 'click'),
          tap(e => {
            e.stopPropagation();
            const target = e.target as HTMLElement;

            if (
              target === $target.current ||
              Array.from($target.current?.querySelectorAll('*') ?? []).includes(
                target,
              )
            ) {
              const hidden =
                $popover.current?.getAttribute('aria-hidden') ?? true;

              hidden === true ? show() : hide();
            } else if (
              target != $popover.current &&
              target.closest('[role="tooltip"]') != $popover.current
            ) {
              hide();
            }
          }),
        )
        .subscribe(),

      fromEvent<MouseEvent>(window, 'contextmenu')
        .pipe(
          filter(() => !disabled),
          filter(() => trigger === 'contextmenu'),
          tap(e => {
            if (e.target === $target.current) {
              e.preventDefault();
              show();
            } else {
              hide();
            }
          }),
          switchMap(() =>
            fromEvent(window, 'click').pipe(
              filter(e => e.target !== $popover.current),
              tap(() => hide()),
            ),
          ),
        )
        .subscribe(),

      fromEvent<MouseEvent>(window, 'mouseover')
        .pipe(
          filter(() => !disabled),
          filter(
            e =>
              trigger === 'hover' &&
              !!(
                e.target === $target.current ||
                $target.current?.contains(e.target as Node)
              ) &&
              $target.current?.querySelectorAll('[role="tooltip"]').length ===
                0,
          ),
          tap(() => show()),
          switchMap(() =>
            fromEvent(window, 'mouseout').pipe(tap(() => hide())),
          ),
        )
        .subscribe(),

      of($target.current)
        .pipe(
          filter(() => !disabled),
          filter(el => trigger === 'focus' && !!el),
          switchMap(el =>
            fromEvent<FocusEvent>(el!, 'focus').pipe(
              tap(() => show()),
              switchMap(() => fromEvent(el!, 'blur').pipe(tap(() => hide()))),
            ),
          ),
        )
        .subscribe(),
    ],
    [disabled, trigger],
  );

  return (
    <VBasePopoverContext.Provider
      value={{
        ...rest,
        disabled,
        placement,
        trigger,
        visible,
        hide,
        show,
        toggle,
        setRef,
        $popover,
        $target,
      }}
    >
      {children}
    </VBasePopoverContext.Provider>
  );
};

VBasePopoverContextProvider.displayName = 'VBasePopoverContextProvider';
